import classNames from "classnames";
import { FormField } from "components/Forms/FormField";
import { LyneDateTimePicker } from "components/Lyne/LyneDateTimePicker";
import { formatDateTime, isDateNewerThanYesterday } from "helpers";
import { createLogger } from "logging";
import { ResourceKey } from "providers";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

const logger = createLogger("HookForm:DateTimePicker");

interface HookFormDateTimePickerProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> extends FormField {
  name: TName;
  control: Control<TFieldValues, unknown, TFieldValues>;
  timeLabel?: ResourceKey;
}

export const HookFormDateTimePicker = <TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  props: HookFormDateTimePickerProps<TFieldValues, TName>
) => {
  const { name, label, timeLabel, control, required, contextHelp } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required,
        validate: (value) => {
          if (!value) return "";
          if (!isDateNewerThanYesterday(value)) return "Date must be newer than yesterday";
          return "";
        },
      }}
      render={({ field, fieldState }) => {
        const { onChange, value, disabled } = field;

        const { isDirty, isTouched, error } = fieldState;
        if (error) logger.debug(`${name}: field has error`, error);

        const className = classNames(
          isDirty ? "is-dirty" : "",
          isTouched ? "is-touched" : "",
          error ? "is-error" : "",
          required && isTouched && !value ? "sbb-invalid" : ""
        );

        // handle onChange event, convert Date value to hook form
        // internal format is dd.MM.yyyy HH:mm
        const onChangeDate = (value: Date | undefined) => {
          if (value) {
            const valueToUse = formatDateTime(value);
            if (valueToUse) {
              logger.debug(`${name}.onChangeDate(): `, value, "->", valueToUse);
              onChange(valueToUse);
              return;
            }
          }

          logger.debug(`${name}.onChangeDate(): empty -> undefined`);
          onChange(undefined);
        };

        return (
          <LyneDateTimePicker
            name={name}
            label={label}
            timeLabel={timeLabel}
            disabled={disabled}
            value={value}
            onChange={onChangeDate}
            className={className}
            dateError={error?.message}
            timeError={error?.message}
            contextHelp={contextHelp}
          />
        );
      }}
    />
  );
};
